import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import UserMap from "./components/UserMap";
import MoneyDashboard from "./components/MoneyDashboard";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import DashboardChart, { ChartData } from "./components/DashboardChart";
import DynamicFilterPanel, { DynamicFilterConfig } from "../../components/DynamicComponent/DynamicFilterPanel";
import usePrivilege from "../../components/CustomHooks/usePrivilege";
import useApi from "../../services/useApi";
import endpoints from "../../services/endpoints";
import MnoAvailableBalance from "./components/MnoAvailableBalance";
import SystemTransactionalPerformance from "./components/SystemTransactionalPerformance";
import MnoTargetAchievement from "./components/MnoTargetAchievement";
import TopMerchantRanking from "./components/TopMerchantRanking";
import TopMerchantRechargeRanking from "./components/TopMerchantRechargeRanking";
import { getMNOColor } from "../../utils/chartColors";
import NotificationPreferenceModal from "../../components/CustomModal/NotificationPreferenceModal";
import { showToast } from "../../components/toastMessage/toastSlice";
import { setUserDetails } from "../../store/slices/userEmail";

// Types for API response
interface MNODataset {
  label: string;
  data: number[];
}

interface MNOSummary {
  total_amount: number;
  total_transactions: number;
  mno_count: number;
}

interface MNOApiResponse {
  labels: string[];
  datasets: MNODataset[];
  summary: MNOSummary;
}

interface ETopUpApiResponse {
  period: string[];
  amount: number[];
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isMapAccess = usePrivilege('D1');
  const isMNOWiseRechargeSalesTrendAccess = usePrivilege('D9');
  const isETopUpStockSalesTrendAccess = usePrivilege('D15');
  const isMNOAvailableBalanceAccess = usePrivilege('D6');
  const isMNOTargetAchievementAccess = usePrivilege('D12');
  const isTopMerchantRankingAccess = usePrivilege('D20'); // Top 3 Merchantwise ETop Up Stock Transfer Ranking
  const isTopMerchantRechargeRankingAccess = usePrivilege('D21'); // Top 10 Merchant Wise Recharge Sales Ranking
  const api = useApi();
  
  // Get logged in user ID and user info from Redux
  const loggedUserId = useSelector((state: RootState) => state.userEmail.loggedUserId);
  const userInfo = useSelector((state: RootState) => state.userEmail.userInfo);
  
  // State for notification preference modal
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationModalLoading, setNotificationModalLoading] = useState(false);
  
  // State for filters and data
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<ChartData>({ labels: [], datasets: [] });
  const [error, setError] = useState<string | undefined>(undefined);
  const hasInitialized = useRef(false);
  
  // State for eTop Up chart
  const [eTopUpLoading, setETopUpLoading] = useState(false);
  const [eTopUpChartData, setETopUpChartData] = useState<ChartData>({ labels: [], datasets: [] });
  const [eTopUpError, setETopUpError] = useState<string | undefined>(undefined);
  
  // Internal search params for filters (not URL-based)
  const [mnoSearchParams, setMnoSearchParams] = useState<URLSearchParams>(new URLSearchParams());
  const [eTopUpSearchParams, setETopUpSearchParams] = useState<URLSearchParams>(new URLSearchParams());
  
  // Dynamic filter configuration for MNO chart
  const mnoFilterConfig: DynamicFilterConfig = {
    dateRangeDropdown: {
      label: "Duration",
      type: "dateRangeDropdown",
      required: false,
      searchKey: { 
        date: "date", 
        start: "start_date", 
        end: "end_date" 
      },
    },
  };

  // Dynamic filter configuration for eTop Up chart
  const eTopUpFilterConfig: DynamicFilterConfig = {
    dateRangeDropdown: {
      label: "Duration",
      type: "dateRangeDropdown",
      required: false,
      searchKey: { 
        date: "date", 
        start: "start_date", 
        end: "end_date" 
      },
    },
  };

  // Function to process MNO data for chart
  const processMNODataForChart = useCallback((apiData: MNOApiResponse): ChartData => {
    const { labels, datasets } = apiData;
    
    // The API now returns chart-ready data, just need to add colors
    const chartDatasets = datasets.map(dataset => {
      const color = getMNOColor(dataset.label);
      return {
        ...dataset,
        borderColor: color,
        backgroundColor: getMNOColor(dataset.label, 0.1),
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBorderWidth: 0
      };
    });
    
    return {
      labels,
      datasets: chartDatasets
    };
  }, []);

  // Function to process eTop Up data for chart
  const processETopUpDataForChart = useCallback((apiData: ETopUpApiResponse): ChartData => {
    const { period, amount } = apiData;
    
    return {
      labels: period,
      datasets: [{
        label: "eTop Up Stock Sales",
        data: amount,
        borderColor: 'rgba(59, 130, 246, 1)', // Blue color
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointBackgroundColor: 'rgba(59, 130, 246, 1)',
        pointBorderColor: 'rgba(59, 130, 246, 1)',
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    };
  }, []);

  // Function to fetch MNO recharge trend data
  const fetchMNORechargeTrend = useCallback(async (filters: { [key: string]: string | string[] | boolean | null }) => {
    setLoading(true);
    setError(undefined);
    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add filter_type (required)
      const filterType = filters.filter_type as string || 'this_year';
      params.append('filter_type', filterType);
      
      // Note: MNO filtering is now handled by the API response structure
      // The API returns all MNOs and the frontend can filter if needed
      
      // Add date range for custom filter
      if (filterType === 'custom') {
        if (filters.start_date) {
          params.append('start_date', filters.start_date as string);
        }
        if (filters.end_date) {
          params.append('end_date', filters.end_date as string);
        }
      }
      
      // Add client timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      params.append('client_timezone', timezone);
      
      const endpoint = `${endpoints.mnoRechargeTrend}?${params.toString()}`;
      const response = await api.getAPI(endpoint);
      
      if (response.error) {
        setError('Failed to fetch MNO recharge trend data');
        return;
      }
      
      const apiData = response.data;
      console.log('MNO API Response:', apiData);
      
      // Process the API data for chart
      const processedData = processMNODataForChart(apiData);
      console.log('MNO Processed Chart Data:', processedData);
      setChartData(processedData);
      
    } catch (err) {
      console.error('Error fetching MNO recharge trend:', err);
      setError('An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  }, [api, processMNODataForChart]);

  // Function to fetch eTop Up stock trend data
  const fetchETopUpStockTrend = useCallback(async (filters: { [key: string]: string | string[] | boolean | null }) => {
    setETopUpLoading(true);
    setETopUpError(undefined);
    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add date range filters
      if (filters.startDate && filters.endDate) {
        params.append('start_date', filters.startDate as string);
        params.append('end_date', filters.endDate as string);
      }
      
      // Add date filter type
      if (filters.date && filters.date !== '') {
        params.append('date', filters.date as string);
      }
      
      // Add status filter
      if (filters.status && filters.status !== '') {
        params.append('status', filters.status as string);
      }
      
      // Add client timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      params.append('client_timezone', timezone);
      
      const endpoint = `${endpoints.etopupStocksTrend}?${params.toString()}`;
      const response = await api.getAPI(endpoint);
      
      if (response.error) {
        setETopUpError('Failed to fetch eTop Up stock trend data');
        return;
      }
      
      const apiData = response.data;
      console.log('eTop Up API Response:', apiData);
      
      // Process the API data for chart
      const processedData = processETopUpDataForChart(apiData);
      console.log('eTop Up Processed Chart Data:', processedData);
      setETopUpChartData(processedData);
      
    } catch (err) {
      console.error('Error fetching eTop Up stock trend:', err);
      setETopUpError('An error occurred while fetching data');
    } finally {
      setETopUpLoading(false);
    }
  }, [api, processETopUpDataForChart]);

  // Filter handlers
  const handleMNOFilterApply = (filters: { [key: string]: string | string[] | boolean | null }) => {
    console.log('MNO Filters applied:', filters);
    
    // Update internal search params
    const newSearchParams = new URLSearchParams(mnoSearchParams);
    
    // Clear existing MNO filter parameters
    newSearchParams.delete('date');
    newSearchParams.delete('start_date');
    newSearchParams.delete('end_date');
    
    // Convert dateRangeDropdown filters to the expected format
    const processedFilters: { [key: string]: string | string[] | boolean | null } = {};
    
    if (filters.date) {
      // Add to internal search params
      newSearchParams.set('date', filters.date as string);
      
      // Map the date range selection to filter_type
      switch (filters.date) {
        case 'Today':
          processedFilters.filter_type = 'today';
          break;
        case 'Yesterday':
          processedFilters.filter_type = 'yesterday';
          break;
        case 'This Month':
          processedFilters.filter_type = 'this_month';
          break;
        case 'This Year':
          processedFilters.filter_type = 'this_year';
          break;
        case 'Custom':
          processedFilters.filter_type = 'custom';
          if (filters.start_date) {
            processedFilters.start_date = dayjs(filters.start_date as string).format('YYYY-MM-DD');
            newSearchParams.set('start_date', filters.start_date as string);
          }
          if (filters.end_date) {
            processedFilters.end_date = dayjs(filters.end_date as string).format('YYYY-MM-DD');
            newSearchParams.set('end_date', filters.end_date as string);
          }
          break;
        default:
          processedFilters.filter_type = 'this_year';
      }
    } else {
      processedFilters.filter_type = 'this_year';
    }
    
    // Update internal search params
    setMnoSearchParams(newSearchParams);
    
    fetchMNORechargeTrend(processedFilters);
  };

  const handleETopUpFilterApply = (filters: { [key: string]: string | string[] | boolean | null }) => {
    console.log('eTop Up Filters applied:', filters);
    
    // Update internal search params
    const newSearchParams = new URLSearchParams(eTopUpSearchParams);
    
    // Clear existing eTop Up filter parameters
    newSearchParams.delete('date');
    newSearchParams.delete('start_date');
    newSearchParams.delete('end_date');
    
    // Convert filters to the expected format for eTop Up API
    const processedFilters: { [key: string]: string | string[] | boolean | null } = {};
    
    if (filters.date) {
      // Map the date range selection to actual dates
      switch (filters.date) {
        case 'Today': {
          processedFilters.date = 'Today';
          newSearchParams.set('date', 'Today');
          break;
        }
        case 'Yesterday': {
          processedFilters.date = 'Yesterday';
          newSearchParams.set('date', 'Yesterday');
          break;
        }
        case 'This Month': {
          processedFilters.date = 'This Month';
          newSearchParams.set('date', 'This Month');
          break;
        }
        case 'This Year': {
          processedFilters.date = 'This Year';
          newSearchParams.set('date', 'This Year');
          break;
        }
        case 'Custom': {
          if (filters.start_date && filters.end_date) {
            // Convert dayjs objects to YYYY-MM-DD format
            const startDate = dayjs(filters.start_date as string).format('YYYY-MM-DD');
            const endDate = dayjs(filters.end_date as string).format('YYYY-MM-DD');
            processedFilters.startDate = startDate;
            processedFilters.endDate = endDate;
            processedFilters.date = 'Custom Date';
            newSearchParams.set('start_date', startDate);
            newSearchParams.set('end_date', endDate);
            newSearchParams.set('date', 'Custom');
          }
          break;
        }
        default: {
          // Default to this year
          processedFilters.date = 'This Year';
          newSearchParams.set('date', 'This Year');
          break;
        }
      }
    } else {
      // Default to this year if no date selected
      processedFilters.date = 'This Year';
      newSearchParams.set('date', 'This Year');
    }
    
    // Update internal search params
    setETopUpSearchParams(newSearchParams);
    
    fetchETopUpStockTrend(processedFilters);
  };
  
  // Handle notification preference save
  const handleNotificationSave = async (notificationType: string) => {
    if (!loggedUserId) {
      dispatch(
        showToast({
          type: "error",
          message: "User ID not found. Please try again.",
        })
      );
      return;
    }

    setNotificationModalLoading(true);
    try {
      const response = await api.PatchAPI(`admins/${loggedUserId}`, {
        notification_type: [notificationType],
      });

      if (!response.error) {
        // Update local user info with the new notification_type
        dispatch(
          showToast({
            type: "success",
            message: `Success! You'll now receive all notifications via ${notificationType}.`,
          })
        );
        // Update the userInfo in Redux to include the notification_type
        const updatedUserInfo = { ...userInfo, notification_type: notificationType };
        dispatch(setUserDetails(updatedUserInfo));
      } else {
        dispatch(
          showToast({
            type: "error",
            message: response.data?.message || "Failed to save notification preference",
          })
        );
      }
    } catch (error) {
      console.error("Error saving notification preference:", error);
      dispatch(
        showToast({
          type: "error",
          message: "An error occurred while saving notification preference",
        })
      );
    } finally {
      setNotificationModalLoading(false);
    }
  };

  // Initialize with default data on component mount
  useEffect(() => {
    // Only navigate to /dashboard if current path is exactly "/"
    if (location.pathname === "/") {
      navigate("/dashboard");
    }
    
    // Check if notification preference has been set
    // Show modal only if notification_type is null or undefined in userInfo
    const hasNotificationType = userInfo?.notification_type;
    
    if (!hasNotificationType && loggedUserId) {
      setShowNotificationModal(true);
    }
    
    // Fetch initial data with default filters only once
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      
      // Use default filters for both charts
      const defaultMnoFilters = {
        filter_type: 'this_year'
      };
      
      const defaultETopUpFilters = {
        startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
        endDate: dayjs().endOf('year').format('YYYY-MM-DD')
      };
      
      fetchMNORechargeTrend(defaultMnoFilters);
      fetchETopUpStockTrend(defaultETopUpFilters);
    }
  }, [location.pathname, navigate, fetchMNORechargeTrend, fetchETopUpStockTrend, loggedUserId, userInfo]);


  return (
    <div className="">
      <Breadcrumbs navPath={[""]} heading="Dashboard" noNavigation list={true} />
      <div className=" flex flex-col h-maintableheight rounded-lg mt-1.5 bg-white relative overflow-y-auto scrollBar">
        {isMapAccess && <UserMap />}
        <MoneyDashboard />
        {/* Transactional report */}
        {(isMNOWiseRechargeSalesTrendAccess || isETopUpStockSalesTrendAccess) && <div>
          <p className="text-2xl font-[600] px-4 pt-4 text-primary_text">Transactional report</p>
          <div className="flex flex-col gap-4 border border-outline rounded-lg p-4 m-4">
            {isMNOWiseRechargeSalesTrendAccess && <div className="w-full">
              <DashboardChart
                type="line"
                data={chartData}
                height={550}
                showLegend={true}
                legendNote="Note: Tap an MNO name to view or hide its chart."
                loading={loading}
                error={error}
                topbar={{
                  title: "MNO Wise Recharge Sales Trend",
                  rightContent: (
                    <div className="flex items-center space-x-3">
                      <DynamicFilterPanel
                        config={mnoFilterConfig}
                        onApply={handleMNOFilterApply}
                        onClear={() => {
                          console.log('MNO filters cleared');
                          // Clear internal search params
                          setMnoSearchParams(new URLSearchParams());
                          
                          const defaultFilters = {
                            filter_type: 'this_year'
                          };
                          fetchMNORechargeTrend(defaultFilters);
                        }}
                        loading={loading}
                        searchParams={mnoSearchParams}
                      />
                    </div>
                  )
                }}
                options={{
                  scales: {
                    x: {
                      grid: {
                        color: 'rgba(0, 0, 0, 0.02)',
                        lineWidth: 1
                      },
                      ticks: {
                        font: {
                          size: 11,
                          family: 'Montserrat, sans-serif',
                          weight: 'bold'
                        },
                        color: '#666'
                      }
                    },
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.02)',
                        lineWidth: 1
                      },
                      ticks: {
                        font: {
                          size: 12,
                          family: 'Montserrat, sans-serif',
                          weight: '600'
                        },
                        color: '#666',
                        callback: function (value: number | string) {
                          return `AFA ${Number(value)}`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>}
            {isETopUpStockSalesTrendAccess && <div className="w-full">
              <DashboardChart
                type="line"
                data={eTopUpChartData}
                height={550}
                showLegend={false}
                loading={eTopUpLoading}
                error={eTopUpError}
                topbar={{
                  title: "eTop Up Stock Sales Trend",
                  rightContent: (
                    <DynamicFilterPanel
                      config={eTopUpFilterConfig}
                      onApply={handleETopUpFilterApply}
                      onClear={() => {
                        console.log('eTop Up filters cleared');
                        // Clear internal search params
                        setETopUpSearchParams(new URLSearchParams());
                        
                        fetchETopUpStockTrend({});
                      }}
                      loading={eTopUpLoading}
                      searchParams={eTopUpSearchParams}
                      className=""
                    />
                  )
                }}
                options={{
                  scales: {
                    x: {
                      grid: {
                        color: 'rgba(0, 0, 0, 0.02)',
                        lineWidth: 1
                      },
                      ticks: {
                        font: {
                          size: 11,
                          family: 'Montserrat, sans-serif',
                          weight: 'bold'
                        },
                        color: '#666'
                      }
                    },
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.02)',
                        lineWidth: 1
                      },
                      ticks: {
                        font: {
                          size: 12,
                          family: 'Montserrat, sans-serif',
                          weight: '600'
                        },
                        color: '#666',
                        callback: function (value: number | string) {
                          return `AFA ${Number(value)}`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>}
          </div>
        </div>}
        {isMNOAvailableBalanceAccess && <MnoAvailableBalance />}
        {!isMNOAvailableBalanceAccess && <div className="mt-6">
          <SystemTransactionalPerformance />
        </div>}
        {isMNOAvailableBalanceAccess && <SystemTransactionalPerformance />}
        {isMNOTargetAchievementAccess && <MnoTargetAchievement />}
        {isTopMerchantRankingAccess && (
          <div className="px-4 pb-4">
            <TopMerchantRanking />
          </div>
        )}
        {isTopMerchantRechargeRankingAccess && (
          <div className="px-4 pb-4">
            <TopMerchantRechargeRanking />
          </div>
        )}
      </div>
      
      {/* Notification Preference Modal */}
      <NotificationPreferenceModal
        isOpen={showNotificationModal}
        onSave={async (notificationType) => {
          await handleNotificationSave(notificationType);
          // Close modal after successful save
          setShowNotificationModal(false);
        }}
        onClose={() => setShowNotificationModal(false)}
        loading={notificationModalLoading}
        closeOnOutsideClick={false}
        buttonText="Submit"
      />
    </div>
  );
}